/**
 * Compare-set filtering — the `cmp` / `compare` param applied to a row set
 * (ADR-0044 · #83).
 *
 * The sibling of `filter-universe.ts` and `filter-book.ts`. All three narrow
 * by one orthogonal axis, all three return a NEW array in every branch
 * including the identity one, and all three are generic over the minimum row
 * shape they need. `?idx=SPX&cmp=AAPL,MSFT&book=1` composes the three.
 *
 * `undefined` — not an empty list — is "filter off". The schema never emits
 * an empty compare set (omitted / blank `cmp=` preprocess to `undefined`),
 * so an empty array here would be a caller bug. We still treat it as match-
 * nothing so a slipped `[]` cannot silently widen to the universe.
 *
 * Identity (unknown → 404, delisted → 410) is NOT this function's job. It
 * intersects; the adapter that knows the universe throws.
 */
import type { FilterKey, Ticker } from '@meridian/contracts';

import { filterBook, type BookedRow, type BookOwnership } from './filter-book';
import { filterUniverse, type IndexedRow } from './filter-universe';

export interface ComparedRow {
  readonly ticker: Ticker;
}

/** Resolved compare allow-list; `undefined` means the filter is off. */
export type CompareOwnership = readonly Ticker[] | undefined;

export function filterCompare<T extends ComparedRow>(
  rows: readonly T[],
  compare: CompareOwnership,
): readonly T[] {
  if (compare === undefined) return [...rows];
  const allow = new Set(compare);
  return rows.filter((row) => allow.has(row.ticker));
}

/**
 * First compare ticker that is not in `present`, in written order. The
 * adapter throws `DatasetNotFoundError` for that id — selectors do not
 * import the port, because a missing name is a repository outcome.
 */
export function unknownCompareTicker(
  present: ReadonlySet<string>,
  compare: CompareOwnership,
): Ticker | undefined {
  if (compare === undefined) return undefined;
  return compare.find((ticker) => !present.has(ticker));
}

/**
 * The three orthogonal axes, composed, BEFORE anything derived. Breadth and
 * sector aggregates run over this pool; narrowing after them would report
 * whole-universe numbers beside a compare-set table.
 */
export function narrowUniverse<T extends IndexedRow & BookedRow>(
  rows: readonly T[],
  index: FilterKey,
  compare: CompareOwnership,
  owned: BookOwnership,
): readonly T[] {
  return filterBook(filterCompare(filterUniverse(rows, index), compare), owned);
}
